import { motion } from 'motion/react';
import { FileSpreadsheet, FileText, Music } from 'lucide-react';
import { ThumbnailImage } from '@/shared/ui/ThumbnailImage';

/**
 * Minimum file shape AssetThumbnail reads. Narrower than ProjectFileResponseDto so callers
 * synthesizing a file-like view (e.g. folder thumbnail cells building from FolderThumbnailDto)
 * can satisfy the contract explicitly without `as unknown as ProjectFileResponseDto` casts.
 *
 * `downloadUrl` / `thumbnailUrl` are typed `unknown` because the generated workspace-api-client
 * emits nullable fields as `{ [key: string]: unknown }` — accepting the wider type lets
 * `ProjectFileResponseDto` satisfy this interface directly. The runtime value is always
 * `string | null | undefined`, narrowed inside this component before use.
 *
 * `thumbnailUrl` is the server-generated preview (first-page render of a PDF/doc, video poster
 * frame). It's optional so file-like views that don't carry one (folder thumbnail cells) still
 * satisfy the contract — they simply fall back to the type icon.
 */
export interface ThumbnailRenderable {
  fileName: string;
  fileType: string;
  assetType: 'image' | 'video' | 'document' | 'spreadsheet' | 'other';
  downloadUrl?: unknown;
  thumbnailUrl?: unknown;
}

interface Props {
  file: ThumbnailRenderable;
  className?: string;
  iconSize?: string;
  fit?: 'cover' | 'contain';
  /**
   * Shared-element morph id (MFINS-3094). When provided, the image/video
   * branch carries this `layoutId` so motion morphs the thumbnail into
   * the opened viewer image (see `getAssetThumbLayoutId`). Only the grid card
   * (`FileAssetCard`) passes it — the filmstrip and folder cells leave it
   * undefined so they don't compete for the same morph target. Non-renderable
   * branches (poster / icon) ignore it and fall back to a plain crossfade.
   *
   * Implementation detail: when `layoutId` is undefined we render plain
   * `<img>` / `<video>` elements instead of `motion.img` / `motion.video`.
   * The motion variants attach layout-projection bookkeeping that
   * fires unintended slide-in / fade-in transitions when the cell mounts,
   * the URL resolves async, or sibling rows reorder — even with no
   * `layoutId` to morph against. Folder rows + filmstrip + any other "no
   * morph wanted" caller now gets a static `<img>` and never animates.
   */
  layoutId?: string;
}

export function AssetThumbnail(props: Props) {
  const { assetType, fileType, fileName } = props.file;
  const { layoutId } = props;
  const downloadUrl = props.file.downloadUrl as string | null | undefined;
  const thumbnailUrl = props.file.thumbnailUrl as string | null | undefined;
  const sizeClass = props.className ?? 'size-16';
  const iconSize = props.iconSize ?? 'size-6';
  const fitClass = props.fit === 'contain' ? 'object-contain' : 'object-cover';

  const isAudio = fileType.startsWith('audio/');
  const isSpreadsheet = assetType === 'spreadsheet';

  let Icon = FileText;
  if (isAudio) Icon = Music;
  if (isSpreadsheet) Icon = FileSpreadsheet;

  // Both the last resort and what an image falls back to when its URL will not
  // load, so it is built before the branches rather than only after them.
  const typeIcon = (
    <div className={`flex items-center justify-center bg-gray-100 ${sizeClass}`}>
      <Icon className={`${iconSize} text-muted-foreground`} />
    </div>
  );

  if (assetType === 'image' && downloadUrl) {
    // The morphing element stays a bare `motion.img`: it is already on screen
    // and about to become the detail view's image, so it must not defer its
    // fetch or fade in under a placeholder.
    return layoutId ? (
      <motion.img
        layoutId={layoutId}
        src={downloadUrl}
        alt={fileName}
        decoding="async"
        className={`${sizeClass} ${fitClass}`}
      />
    ) : (
      <ThumbnailImage
        src={downloadUrl}
        alt={fileName}
        className={sizeClass}
        fit={props.fit}
        fallback={typeIcon}
      />
    );
  }

  if (assetType === 'video' && downloadUrl) {
    return layoutId ? (
      <motion.video
        layoutId={layoutId}
        src={downloadUrl}
        preload="metadata"
        muted
        className={`${sizeClass} ${fitClass}`}
      />
    ) : (
      <video src={downloadUrl} preload="metadata" muted className={`${sizeClass} ${fitClass}`} />
    );
  }

  // Non-renderable files (PDFs, Office docs) get a server-generated preview image
  // when one exists, matching the Library artifact poster treatment. Falls back to
  // the type icon below while extraction is pending or unsupported.
  if (thumbnailUrl) {
    return (
      <ThumbnailImage
        src={thumbnailUrl}
        alt={fileName}
        className={sizeClass}
        fit={props.fit}
        fallback={typeIcon}
      />
    );
  }

  return typeIcon;
}
